/**
 * NotificationCenter — Saatten açılan sağ slide-in panel (~340px)
 * Bugün widget | Quick Todos | Son chat | Aktif agents | FLZ kazanç
 * Modüler: kendi hook'u, kendi data'sı
 */
import { Brain, Calendar, CheckCircle, MessageCircle, Sparkles, TrendingUp, Users, X, Zap } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { API_URL } from '../../../config/api'
import { useNotificationCenterData } from './useNotificationCenterData'
import { useNotificationEvents } from './useNotificationEvents'

interface Props {
  open: boolean
  onClose: () => void
}

export default function NotificationCenter({ open, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const { recentMessages, activeAgents, flzToday, loading } = useNotificationCenterData()
  const { liveNotifications, unreadCount, markRead, markAllRead } = useNotificationEvents()
  const now = new Date()

  // Dışına tıklayınca kapat
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    // Küçük delay — açma tıklamasını yakalamasın
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 100)
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler) }
  }, [open, onClose])

  // ESC ile kapat
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  const sectionTitle = (icon: React.ReactNode, title: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, marginTop: 18 }}>
      {icon}
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--os-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
    </div>
  )

  const card = (children: React.ReactNode) => (
    <div style={{
      background: 'var(--os-input-bg)', border: '1px solid var(--os-border)',
      borderRadius: 12, padding: 12, marginBottom: 8,
    }}>{children}</div>
  )

  return (
    <div ref={ref} style={{
      position: 'fixed', top: 30, right: 0, bottom: 0, width: 340,
      background: 'var(--os-win-bg)', backdropFilter: 'blur(24px)',
      borderLeft: '1px solid var(--os-border)',
      boxShadow: '-8px 0 30px rgba(0,0,0,0.3)',
      zIndex: 9998, overflowY: 'auto', overflowX: 'hidden',
      padding: '20px 16px',
      animation: 'ncSlideIn 0.25s ease-out',
    }}>
      <style>{`
        @keyframes ncSlideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>

      {/* Header — close button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 42, fontWeight: 200, color: 'var(--os-text)', lineHeight: 1 }}>{timeStr}</div>
          <div style={{ fontSize: 13, color: 'var(--os-muted)', marginTop: 4 }}>{dateStr}</div>
        </div>
        <button onClick={onClose} style={{
          background: 'var(--os-border)', border: 'none', borderRadius: 8,
          width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--os-muted)',
        }}><X size={14} /></button>
      </div>

      {/* FLZ Bugün */}
      {sectionTitle(<Zap size={12} color="var(--os-warning)" />, 'FLZ Bugün')}
      {card(
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--os-warning)' }}>{flzToday} FLZ</div>
            <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>Bugünkü kazanç</div>
          </div>
          <TrendingUp size={20} color="var(--os-warning)" style={{ opacity: 0.5 }} />
        </div>
      )}

      {/* Live Events — OS event'lerinden gelen bildirimler */}
      {liveNotifications.length > 0 && (
        <>
          {sectionTitle(
            <Sparkles size={12} color="var(--os-accent)" />,
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Live Events
              {unreadCount > 0 && (
                <span style={{ fontSize: 8, background: 'var(--os-danger)', color: '#fff', borderRadius: 6, padding: '1px 5px', fontWeight: 700 }}>
                  {unreadCount}
                </span>
              )}
              <button onClick={markAllRead} style={{ fontSize: 9, color: 'var(--os-muted)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', marginLeft: 'auto' }}>
                Mark read
              </button>
            </span> as any
          )}
          {liveNotifications.slice(0, 8).map((n: { id: string; type: string; title: string; message: string; read: boolean; actionEvent?: string }) => {
            const colors: Record<string, string> = { task: 'var(--os-success)', skill: 'var(--os-warning)', research: '#8b5cf6', social: 'var(--os-info)', flzhub: '#ec4899', system: '#6b7280' }
            const icons: Record<string, typeof CheckCircle> = { task: CheckCircle, skill: Zap, research: Sparkles, social: MessageCircle, flzhub: Sparkles, system: CheckCircle }
            const Icon = icons[n.type] ?? CheckCircle
            const color = colors[n.type] ?? '#6b7280'
            return card(
              <div key={n.id} onClick={() => {
                markRead(n.id)
                if (n.actionEvent) {
                  window.dispatchEvent(new CustomEvent('runai:open-window', { detail: n.actionEvent }))
                  onClose()
                }
              }} style={{ cursor: n.actionEvent ? 'pointer' : 'default', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: `${color}12`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={12} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: n.read ? 400 : 700, color: 'var(--os-text)' }}>{n.title}</div>
                  <div style={{ fontSize: 10, color: 'var(--os-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>
                </div>
                {!n.read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 6 }} />}
              </div>
            )
          })}
        </>
      )}

      {/* Aktif Agents */}
      {sectionTitle(<Users size={12} color="var(--os-success)" />, `Aktif Agents (${activeAgents.length})`)}
      {loading ? (
        card(<div style={{ fontSize: 12, color: 'var(--os-muted)' }}>Loading...</div>)
      ) : activeAgents.length === 0 ? (
        card(<div style={{ fontSize: 12, color: 'var(--os-muted)' }}>Active agent yok</div>)
      ) : (
        card(
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activeAgents.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: a.status === 'running' ? '#f59e0b' : a.status === 'thinking' ? '#6366f1' : '#10b981',
                  boxShadow: `0 0 4px ${a.status === 'running' ? '#f59e0b' : '#10b981'}`,
                }} />
                <span style={{ fontSize: 12, color: 'var(--os-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.agent_name}
                </span>
                <span style={{ fontSize: 10, color: 'var(--os-muted)' }}>{a.status}</span>
              </div>
            ))}
          </div>
        )
      )}

      {/* Son Chat Mesajları */}
      {sectionTitle(<MessageCircle size={12} color="#818cf8" />, 'Son Mesajlar')}
      {loading ? (
        card(<div style={{ fontSize: 12, color: 'var(--os-muted)' }}>Loading...</div>)
      ) : recentMessages.length === 0 ? (
        card(<div style={{ fontSize: 12, color: 'var(--os-muted)' }}>Henüz mesaj yok</div>)
      ) : (
        recentMessages.map(m => (
          card(
            <div key={m.id}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--os-accent)', marginBottom: 3 }}>{m.agent_name}</div>
              <div style={{
                fontSize: 12, color: 'var(--os-text)', lineHeight: 1.4,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{m.message}</div>
            </div>
          )
        ))
      )}

      {/* Takvim mini */}
      {sectionTitle(<Calendar size={12} color="#06b6d4" />, 'Today')}
      {card(
        <div style={{ fontSize: 12, color: 'var(--os-muted)', textAlign: 'center', padding: '8px 0' }}>
          No tasks — enjoy your day ✨
        </div>
      )}

      {/* Twin Weekly Report */}
      {sectionTitle(<Brain size={12} color="#ec4899" />, 'Twin Report')}
      <TwinReportCard />
    </div>
  )
}

function TwinReportCard() {
  const [report, setReport] = useState<any>(null)
  useEffect(() => {
    const token = localStorage.getItem('token') || ''
    if (!token) return
    fetch(`${API_URL}/api/twin/weekly-report`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.report) setReport(d.report) })
      .catch(() => {})
  }, [])

  if (!report) return (
    <div style={{ background: 'var(--os-input-bg)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--os-muted)' }}>No twin report yet</div>
    </div>
  )

  const s = report.stats
  return (
    <div style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.15)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#ec4899' }}>This Week</span>
        <span style={{ fontSize: 9, color: 'var(--os-muted)' }}>{s.stage}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, fontSize: 10 }}>
        <div><span style={{ fontWeight: 700, color: 'var(--os-text)' }}>{s.new_memories}</span> <span style={{ color: 'var(--os-muted)' }}>memories</span></div>
        <div><span style={{ fontWeight: 700, color: 'var(--os-text)' }}>{s.new_inferences}</span> <span style={{ color: 'var(--os-muted)' }}>inferences</span></div>
        <div><span style={{ fontWeight: 700, color: 'var(--os-text)' }}>{s.messages_sent}</span> <span style={{ color: 'var(--os-muted)' }}>messages</span></div>
      </div>
      {report.growth && (
        <div style={{ marginTop: 6, fontSize: 9, color: 'var(--os-muted)' }}>
          Persona: {report.growth.persona_completeness}% · Skills: {report.growth.skills_used}
        </div>
      )}
    </div>
  )
}
